import React, { useState, useRef, useEffect } from 'react';
import { FaPalette, FaSun, FaMoon, FaCheck } from 'react-icons/fa';
import { useTheme } from '../context/SocketContext';

const themePreviews = {
  blue: ['#3b82f6', '#f97316'],
  orange: ['#f97316', '#3b82f6'],
  purple: ['#8b5cf6', '#f97316'],
  green: ['#10b981', '#3b82f6'],
  pink: ['#ec4899', '#3b82f6'],
  teal: ['#14b8a6', '#f97316'],
  red: ['#ef4444', '#3b82f6'],
};

const ThemeCustomizer = ({ variant = 'default' }) => {
  const { isDarkMode, toggleTheme, colorTheme, setColorTheme, colorThemes, currentThemeColors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  const triggerClassName = variant === 'auth'
    ? 'relative p-3 rounded-xl bg-white dark:bg-dark-800 shadow-lg border border-gray-200 dark:border-dark-700 hover:scale-105 transition-all duration-200 active:scale-95 group'
    : 'relative p-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-300 active:scale-95 group';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const availableThemes = Object.entries(colorThemes).map(([id, theme]) => ({
    id,
    name: theme.name,
    preview: themePreviews[id] || [theme.primary, theme.accent],
  }));

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={triggerClassName}
        aria-label="Appearance settings"
        title="Appearance settings"
      >
        <div className="relative w-5 h-5">
          <FaPalette
            className="w-5 h-5 transition-all duration-300 group-hover:rotate-12"
            style={{ color: currentThemeColors?.primary }}
          />
          <span
            className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white dark:border-dark-800 flex items-center justify-center"
            style={{ background: isDarkMode ? 'var(--primary-600)' : 'var(--accent-500)' }}
          >
            {isDarkMode ? (
              <FaMoon className="w-2 h-2 text-white" />
            ) : (
              <FaSun className="w-2 h-2 text-white" />
            )}
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white dark:bg-dark-800 rounded-2xl shadow-xl border-2 border-gray-200 dark:border-dark-700 overflow-hidden animate-scale-in z-50">
          <div
            className="px-4 py-3 text-white"
            style={{ background: 'linear-gradient(135deg, var(--primary-600), var(--primary-500))' }}
          >
            <h3 className="font-semibold text-sm">Appearance</h3>
            <p className="text-xs text-white/80 mt-0.5">Theme color and light/dark mode</p>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Color Mode
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => isDarkMode && toggleTheme()}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    !isDarkMode
                      ? 'bg-primary-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                  }`}
                >
                  <FaSun className="w-4 h-4" />
                  Light
                </button>
                <button
                  onClick={() => !isDarkMode && toggleTheme()}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isDarkMode
                      ? 'bg-primary-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                  }`}
                >
                  <FaMoon className="w-4 h-4" />
                  Dark
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Color Theme
              </p>
              <div className="grid grid-cols-2 gap-2">
                {availableThemes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => setColorTheme(theme.id)}
                    className={`relative flex items-center gap-2.5 p-2.5 rounded-xl text-left transition-all ${
                      colorTheme === theme.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500'
                        : 'bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600'
                    }`}
                  >
                    <div className="flex -space-x-1 shrink-0">
                      {theme.preview.map((color, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded-full border-2 border-white dark:border-dark-800 shadow-sm"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <p className="text-xs font-semibold text-gray-900 dark:text-white truncate flex-1">
                      {theme.name}
                    </p>
                    {colorTheme === theme.id && (
                      <FaCheck className="w-3 h-3 text-primary-500 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeCustomizer;
